import {Dispatch, SetStateAction, useCallback, useEffect, useRef} from "react";
import {Camera} from "./camera";
import {Vec2} from "./math/vec2";

export type UseZoomControls = {onWheel: (ev: React.WheelEvent) => void};

export function useZoomControls(setCamera: Dispatch<SetStateAction<Camera>>): UseZoomControls {
  const onWheel = useCallback(
    (ev: React.WheelEvent) => {
      ev.preventDefault();
      const direction = ev.deltaY > 0 ? -1 : 1;

      setCamera(prev => {
        const next = prev.clone();
        next.targetZoomStep += direction;
        return next;
      });
    },
    [setCamera],
  );

  return {onWheel};
}

export type UsePanControls = {
  onPointerDown: (ev: React.PointerEvent) => void;
  onPointerUp: (ev: React.PointerEvent) => void;
  onPointerMove: (ev: React.PointerEvent) => void;
};

export function usePanControls(
  allowPanning: boolean,
  setCamera: Dispatch<SetStateAction<Camera>>,
  onIsPanningChange: (isPanning: boolean) => void,
): UsePanControls {
  const isDragging = useRef(false);

  /** Previous position of the mouse, while dragging, in viewport space. */
  const prevPos = useRef<Vec2>(new Vec2(0, 0));
  /** Camera drag velocity, in world space. */
  const velocity = useRef<Vec2>(new Vec2(0, 0));

  // Handle mouse/touch drag
  const onPointerDown = useCallback(
    (ev: React.PointerEvent) => {
      if (!allowPanning) {
        return;
      }

      ev.preventDefault();

      isDragging.current = true;

      onIsPanningChange(true);

      velocity.current = Vec2.zero();
      prevPos.current = new Vec2(ev.clientX, ev.clientY);

      setCamera(prev => {
        const next = prev.clone();
        next.targetPosition = null;
        return next;
      });
    },
    [allowPanning, onIsPanningChange, setCamera],
  );

  const onPointerMove = useCallback(
    (ev: React.PointerEvent) => {
      if (isDragging.current) {
        ev.preventDefault();

        const pos = new Vec2(ev.clientX, ev.clientY);
        const delta = pos.sub(prevPos.current);

        setCamera(prev => {
          const next = prev.clone();
          const worldDelta = delta.divScalar(next.zoomFactor);

          next.position = next.position.sub(worldDelta);
          velocity.current = worldDelta.negate();

          return next;
        });

        prevPos.current = pos;
      }
    },
    [setCamera],
  );

  const onPointerUp = useCallback(
    (ev: React.PointerEvent) => {
      if (isDragging.current) {
        ev.preventDefault();
      }

      isDragging.current = false;
      onIsPanningChange(false);
    },
    [onIsPanningChange],
  );

  // Animation loop for smooth camera deceleration
  useEffect(() => {
    let animationId: number;
    let lastTime = performance.now();

    const tick = (time: number) => {
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      // Smoothly slow down the camera after it has been released.
      if (!isDragging.current && velocity.current.lengthSquared() > 0.0) {
        velocity.current = velocity.current.smoothNudge(Vec2.zero(), 8, dt, 1e-3);

        // Apply camera velocity to position
        setCamera(prev => {
          const next = prev.clone();
          next.position = prev.position.add(velocity.current);
          return next;
        });
      }

      animationId = requestAnimationFrame(tick);
    };

    animationId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationId);
  }, [setCamera]);

  return {onPointerDown, onPointerMove, onPointerUp};
}
